"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Crown } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

const majorArcanaCards = [
  { id: 0, name: "The Fool", turkish: "Deli", keyword: "Yeni başlangıçlar", image: "rider-waite-fool.svg" },
  { id: 1, name: "The Magician", turkish: "Büyücü", keyword: "İrade ve manifestasyon", image: "rider-waite-magician.svg" },
  { id: 2, name: "The High Priestess", turkish: "Yüksek Rahibe", keyword: "Sezgi ve gizli bilgi", image: "rider-waite-high-priestess.svg" },
  { id: 3, name: "The Empress", turkish: "İmparatoriçe", keyword: "Bereket ve yaratıcılık", image: "rider-waite-empress.svg" },
  { id: 4, name: "The Emperor", turkish: "İmparator", keyword: "Otorite ve yapı", image: "rider-waite-emperor.svg" },
  { id: 5, name: "The Hierophant", turkish: "Hierofant", keyword: "Gelenek ve öğretim", image: "rider-waite-hierophant.svg" },
  { id: 6, name: "The Lovers", turkish: "Aşıklar", keyword: "Aşk ve seçimler", image: "rider-waite-lovers.svg" },
  { id: 7, name: "The Chariot", turkish: "Savaş Arabası", keyword: "Zafer ve kontrol", image: "rider-waite-chariot.svg" },
  { id: 8, name: "Strength", turkish: "Güç", keyword: "İç güç ve cesaret", image: "rider-waite-strength.svg" },
  { id: 9, name: "The Hermit", turkish: "Ermiş", keyword: "İçsel arayış", image: "rider-waite-hermit.svg" },
  { id: 10, name: "Wheel of Fortune", turkish: "Kader Çarkı", keyword: "Değişim ve döngüler", image: "rider-waite-wheel-of-fortune.svg" },
  { id: 11, name: "Justice", turkish: "Adalet", keyword: "Denge ve adalet", image: "rider-waite-justice.svg" },
  { id: 12, name: "The Hanged Man", turkish: "Asılı Adam", keyword: "Fedakarlık ve bekleme", image: "rider-waite-hanged-man.svg" },
  { id: 13, name: "Death", turkish: "Ölüm", keyword: "Dönüşüm ve yeniden doğuş", image: "rider-waite-death.svg" },
  { id: 14, name: "Temperance", turkish: "Ölçülülük", keyword: "Denge ve uyum", image: "rider-waite-temperance.svg" },
  { id: 15, name: "The Devil", turkish: "Şeytan", keyword: "Bağımlılık ve ilüzyon", image: "rider-waite-devil.svg" },
  { id: 16, name: "The Tower", turkish: "Kule", keyword: "Ani değişim ve çöküş", image: "rider-waite-tower.svg" },
  { id: 17, name: "The Star", turkish: "Yıldız", keyword: "Umut ve rehberlik", image: "rider-waite-star.svg" },
  { id: 18, name: "The Moon", turkish: "Ay", keyword: "İllüzyon ve bilinçsiz", image: "rider-waite-moon.svg" },
  { id: 19, name: "The Sun", turkish: "Güneş", keyword: "Neşe ve başarı", image: "rider-waite-sun.svg" },
  { id: 20, name: "Judgement", turkish: "Yargı", keyword: "Yeniden diriliş", image: "rider-waite-judgement.svg" },
  { id: 21, name: "The World", turkish: "Dünya", keyword: "Tamamlanma ve bütünlük", image: "rider-waite-world.svg" }
];

export default function MajorArcanaPage() {
  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-purple-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-purple-500">/</span>
            <span className="text-sm">Rider-Waite</span>
            <span className="text-purple-500">/</span>
            <span className="text-sm">Majör Arkana</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            AXTROLOG Nedir
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Tarot Sistemleri
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/rider-waite"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Rider-Waite
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <span className="text-slate-300">Majör Arkana</span>
        </div>

        {/* Header */}
        <div className="bg-gradient-to-r from-purple-900/20 to-indigo-900/20 rounded-xl p-8 mb-8 border border-purple-500/20">
          <h1 className="text-4xl font-bold text-slate-100 mb-4 flex items-center gap-3">
            <Star className="h-10 w-10 text-purple-400" />
            Majör Arkana Kartları
          </h1>
          <p className="text-lg text-slate-200 leading-relaxed">
            Rider-Waite-Smith tarot destesinin 22 Majör Arkana kartı. Her biri yaşamın büyük derslerini ve
            ruhsal yolculuğun aşamalarını temsil eder.
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
          {majorArcanaCards.map((card) => (
            <Link
              key={card.id}
              href={`/about/tarot/systems/rider-waite/major-arcana/${card.name.toLowerCase().replace(/\s+/g, '-').replace('the-', '')}`}
              className="block"
            >
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 hover:border-purple-500/50 transition-all duration-300 cursor-pointer group shadow-lg hover:shadow-xl hover:scale-[1.02] h-full">
                <div className="text-center">
                  <div className="w-24 h-36 mx-auto mb-4 rounded-lg overflow-hidden group-hover:scale-105 transition-transform duration-300 shadow-lg">
                    <img
                      src={`/images/tarot/cards/${card.image}`}
                      alt={card.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        // Fallback to number circle if image fails to load
                        e.currentTarget.style.display = 'none';
                        const sibling = e.currentTarget.nextElementSibling as HTMLElement;
                        if (sibling) {
                          sibling.style.display = 'flex';
                        }
                      }}
                    />
                    <div className="w-full h-full bg-gradient-to-br from-purple-500 to-indigo-700 rounded-lg flex items-center justify-center" style={{display: 'none'}}>
                      <span className="text-white font-bold text-lg">{card.id === 0 ? '0' : card.id}</span>
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-slate-100 mb-2">{card.name}</h3>
                  <p className="text-slate-400 text-sm mb-3">{card.turkish}</p>
                  <p className="text-purple-400 text-sm font-medium">{card.keyword}</p>
                </div>
                <div className="mt-4 pt-4 border-t border-slate-700">
                  <span className="text-purple-400 text-sm group-hover:translate-x-1 transition-transform duration-300 inline-block">
                    Kartı İncele →
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Info Section */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-4">Majör Arkana Hakkında</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3">Ruhsal Yolculuk</h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                Majör Arkana kartları, "Fool's Journey" olarak bilinen ruhsal gelişim yolculuğunu anlatır.
                The Fool (0) ile başlayıp The World (XXI) ile tamamlanan bu yolculuk, insanın yaşam
                boyunca karşılaştığı büyük dersleri ve dönüşümleri simgeler.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3">Rider-Waite Özellikleri</h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                RWS destesinde Strength (VIII) ve Justice (XI) kartlarının yerleri geleneksel sıraya göre
                değiştirilmiştir. Bu değişiklik Golden Dawn öğretilerine dayalı astrolojik correspondence'lara
                uyum sağlamak için yapılmıştır.
              </p>
            </div>
          </div>
        </div>

        {/* Important Note */}
        <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6 mb-8">
          <h3 className="text-lg font-semibold text-amber-200 mb-3">⚠️ Önemli Uyarı</h3>
          <p className="text-amber-100 leading-relaxed">
            Tarot kartları sembolik yorumlama araçlarıdır ve kesinlik iddiası taşımazlar. İçerikler tamamen
            merak, eğlence ve kişisel farkındalık amaçlıdır. Yaşamsal kararlar için uzman görüşü alınmalıdır.
          </p>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/systems/rider-waite"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Rider-Waite Sistemi
          </Link>

          <Link
            href="/about/tarot/systems/rider-waite/minor-arcana"
            className="inline-flex items-center px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors"
          >
            Minör Arkana
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}